/**
 * CESM OSINT System - Ticker Banner (Téléscripteur)
 * Bandeau déroulant style télex affichant les sources recommandées
 * 
 * Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel © 2026
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Radio, AlertCircle, ExternalLink } from 'lucide-react';

// Sources recommandées avec leur niveau d'accès actuel
const RECOMMENDED_SOURCES = [
  { name: "Financial Times", access: "titres uniquement", priority: "haute", category: "géopolitique" },
  { name: "The Economist", access: "résumés limités", priority: "haute", category: "géopolitique" },
  { name: "Janes Intelligence", access: "non connecté", priority: "critique", category: "défense" },
  { name: "Lloyd's List Intelligence", access: "flux RSS basique", priority: "haute", category: "maritime" },
  { name: "S&P Platts Premium", access: "données partielles", priority: "haute", category: "énergie" },
  { name: "Argus Media", access: "non connecté", priority: "moyenne", category: "énergie" },
  { name: "Meta-Défense", access: "abonnement requis", priority: "moyenne", category: "défense FR" },
  { name: "Armées.com", access: "accès libre", priority: "connecté", category: "défense FR" },
];

const TickerBanner = ({ onSourceClick }) => {
  const [isPaused, setIsPaused] = useState(false);
  
  // Construire le texte du ticker
  const tickerText = RECOMMENDED_SOURCES.map(src => {
    const statusIcon = src.priority === "connecté" ? "✓" : src.priority === "critique" ? "⚠" : "○";
    return `${statusIcon} ${src.name} (${src.access})`;
  }).join("  •  ");
  
  // Doubler le texte pour un défilement continu
  const fullText = `${tickerText}  |||  ${tickerText}`;

  return (
    <div 
      className="relative overflow-hidden bg-gradient-to-r from-amber-900/80 via-yellow-800/80 to-amber-900/80 border-y border-yellow-500/50"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Label fixe à gauche - avec z-index et fond opaque */}
      <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center px-4 bg-amber-900" style={{ minWidth: '200px' }}>
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-yellow-400 animate-pulse" />
          <span className="font-mono text-xs text-yellow-300 font-bold uppercase tracking-wider whitespace-nowrap">
            SOURCES À CONNECTER
          </span>
        </div>
        {/* Dégradé de fondu */}
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-r from-amber-900 to-transparent"></div>
      </div>
      
      {/* Texte défilant - avec marge gauche suffisante */}
      <div className="py-2 ml-52 overflow-hidden">
        <motion.div
          className="whitespace-nowrap"
          animate={{
            x: isPaused ? 0 : [0, -50 * RECOMMENDED_SOURCES.length * 4],
          }}
          transition={{
            x: {
              duration: 60,
              repeat: Infinity,
              ease: "linear",
            },
          }}
          style={{ willChange: 'transform' }}
        >
          <span className="font-mono text-sm text-yellow-100 tracking-wide">
            {RECOMMENDED_SOURCES.map((src, idx) => (
              <span key={idx} className="inline-flex items-center">
                <span className={`mr-1 ${
                  src.priority === "connecté" ? "text-green-400" : 
                  src.priority === "critique" ? "text-red-400" : 
                  "text-yellow-400"
                }`}>
                  {src.priority === "connecté" ? "✓" : src.priority === "critique" ? "⚠" : "○"}
                </span>
                <span 
                  className="hover:text-white hover:underline cursor-pointer"
                  onClick={() => onSourceClick && onSourceClick(src)}
                >
                  {src.name}
                </span>
                <span className="text-yellow-400/70 mx-1">({src.access})</span>
                <span className="text-yellow-600 mx-3">•</span>
              </span>
            ))}
            <span className="text-yellow-500 mx-4">|||</span>
            {RECOMMENDED_SOURCES.map((src, idx) => (
              <span key={`dup-${idx}`} className="inline-flex items-center">
                <span className={`mr-1 ${
                  src.priority === "connecté" ? "text-green-400" : 
                  src.priority === "critique" ? "text-red-400" : 
                  "text-yellow-400"
                }`}>
                  {src.priority === "connecté" ? "✓" : src.priority === "critique" ? "⚠" : "○"}
                </span>
                <span 
                  className="hover:text-white hover:underline cursor-pointer"
                  onClick={() => onSourceClick && onSourceClick(src)}
                >
                  {src.name}
                </span>
                <span className="text-yellow-400/70 mx-1">({src.access})</span>
                <span className="text-yellow-600 mx-3">•</span>
              </span>
            ))}
          </span>
        </motion.div>
      </div>
      
      {/* Indicateur pause */}
      {isPaused && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
          <span className="font-mono text-xs text-yellow-400 bg-black/50 px-2 py-1 rounded">
            PAUSE
          </span>
        </div>
      )}
    </div>
  );
};

export default TickerBanner;
